import {
	DateSingleValueTypes,
	DateSubType,
	DateType,
	DoesNotRequireDate,
	RequiresSingleDate,
	SystemDefined,
} from './constants';
import {dateRuleDefaults} from './defaults';
import {DateRule} from './types';

type InBetween = {
	fromDate: string;
	toDate: string;
};

type SingleValue = {
	date: string;
};

export type DateRuleBaseParams<
	T extends DateType,
	T2 extends DateSubType = DateSubType,
> = {
	type: T;
	subType: T2;
} & ([T2] extends [DateSubType.IN_BETWEEN]
	? InBetween
	: [T2] extends [DateSubType.AFTER | DateSubType.BEFORE]
	? SingleValue
	: {});

type SystemDefinedBase<T extends DateSubType = DateSubType> =
	DateRuleBaseParams<SystemDefined, T>;

type SystemDefinedInBetween = SystemDefinedBase<DateSubType.IN_BETWEEN>;

type SystemDefinedBeforeOrAfter = SystemDefinedBase<RequiresSingleDate>;
type SystemDefinedAllOthers = SystemDefinedBase<DoesNotRequireDate>;

type UserDefinedBase<T extends DateSubType = DateSubType> = DateRuleBaseParams<
	DateType.USER_DEFINED_ATTRIBUTE,
	T
> & {
	attributeName: string;
};
type UserDefinedInBetween = UserDefinedBase<DateSubType.IN_BETWEEN>;
type UserDefinedBeforeOrAfter = UserDefinedBase<RequiresSingleDate>;
type UserDefinedAllOthers = UserDefinedBase<DoesNotRequireDate>;

type GetDateRuleParams =
	| UserDefinedInBetween
	| UserDefinedBeforeOrAfter
	| UserDefinedAllOthers
	| SystemDefinedInBetween
	| SystemDefinedBeforeOrAfter
	| SystemDefinedAllOthers;

// user defined, range needed
export function createDateRule(
	params: UserDefinedInBetween,
): DateRule<DateType.USER_DEFINED_ATTRIBUTE, DateSubType.IN_BETWEEN>;
// user defined, single date needed
export function createDateRule(
	params: UserDefinedBeforeOrAfter,
): DateRule<DateType.USER_DEFINED_ATTRIBUTE, RequiresSingleDate>;
// user defined, no date needed
export function createDateRule(
	params: UserDefinedAllOthers,
): DateRule<DateType.USER_DEFINED_ATTRIBUTE, DoesNotRequireDate>;

// system, range needed
export function createDateRule(
	params: SystemDefinedInBetween,
): DateRule<SystemDefined, DateSubType.IN_BETWEEN>;
// system, single date needed
export function createDateRule(
	params: SystemDefinedBeforeOrAfter,
): DateRule<SystemDefined, RequiresSingleDate>;
// system, no date value neeeded
export function createDateRule(
	params: SystemDefinedAllOthers,
): DateRule<SystemDefined, DoesNotRequireDate>;

export function createDateRule(params: GetDateRuleParams) {
	const {subType, type} = params;
	return {
		...dateRuleDefaults,
		elementSubType1: type,
		elementSubType2: subType,
		elementValue1:
			subType === DateSubType.IN_BETWEEN
				? params.fromDate
				: // my least favorite things about constants and includes
				DateSingleValueTypes.includes(
						subType as typeof DateSingleValueTypes[number],
				  )
				? (params as SingleValue).date
				: '',
		elementValue2: subType === DateSubType.IN_BETWEEN ? params.toDate : '',
		elementValue3:
			type === DateType.USER_DEFINED_ATTRIBUTE ? params.attributeName : '',
	};
}

export default createDateRule;
